import {createContext, use, useEffect, useRef, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'

import {getDiffSizeCategory, type DiffSizeCategory} from '../helpers/get-diff-size-category'
import {useDiffstatRouterData} from '../page-data/loaders/use-diffstat-data'

export type InitialStateData = {
  pullRequestId: string | number
  baseSha: string | undefined
  headSha: string | undefined
  diffVersion: 'v1' | 'v2'
  virtualizationEnabled: boolean
}
type State = InitialStateData & {
  pullRequestId: string
  diffSizeCategory: DiffSizeCategory
}
type PullRequestAnalyticsStore = ReturnType<typeof createPullRequestAnalyticsStore>

const PullRequestAnalyticsStoreContext = createContext<PullRequestAnalyticsStore | null>(null)

export function usePullRequestAnalyticsStore<T>(selector: (state: State) => T): T {
  const store = use(PullRequestAnalyticsStoreContext)
  if (!store) throw new Error('Missing PullRequestAnalyticsStoreProvider in the tree')
  return useStore(store, selector)
}

type PullRequestAnalyticsStoreProviderProps = {
  children?: ReactNode
  initData: InitialStateData
}
export const PullRequestAnalyticsStoreProvider = ({initData, children}: PullRequestAnalyticsStoreProviderProps) => {
  const storeRef = useRef<PullRequestAnalyticsStore | null>(null)
  const diffstat = useDiffstatRouterData()

  // eslint-disable-next-line react-hooks/refs
  if (!storeRef.current) {
    storeRef.current = createPullRequestAnalyticsStore(initData)
  }

  useEffect(() => {
    if (diffstat) {
      storeRef?.current?.setState({diffSizeCategory: getDiffSizeCategory(diffstat.linesChanged)})
    }
  }, [diffstat])

  // eslint-disable-next-line react-hooks/refs
  return <PullRequestAnalyticsStoreContext value={storeRef.current}>{children}</PullRequestAnalyticsStoreContext>
}

const createPullRequestAnalyticsStore = (initStateData: InitialStateData) => {
  return createStore<State>()(
    devtools(
      () => ({
        ...initStateData,
        pullRequestId: initStateData.pullRequestId.toString(),
        diffSizeCategory: 'unknown',
      }),
      {name: 'analytics-store'},
    ),
  )
}

try{ PullRequestAnalyticsStoreContext.displayName ||= 'PullRequestAnalyticsStoreContext' } catch {}
try{ PullRequestAnalyticsStoreProvider.displayName ||= 'PullRequestAnalyticsStoreProvider' } catch {}